import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';

import { RequireSerialNumberName, RequireSerialNumberEnabledValue } from '../../products/product-details/constants/product-options';
import Global from '../../store/global-selectors';

import type { SerializableLineItem } from './use-has-missing-required-serials';

type ProductOption = {
  name: string;
  value?: string;
};

type ProductWithOptions = {
  productId?: number | string;
  productOptions?: ProductOption[];
};

type FulfillmentPlanItem = {
  // Matches the canonical FulfillmentPlanItem type (client-common): the id is
  // stored as a string in Redux state, not a number — must be coerced with
  // Number(...) before it's used as a Map key against SerialNumberProvider's
  // getSerialNumbers, which is keyed by the numeric orderItemId from the API.
  fulfillmentPlanItemId: string | null;
  productId?: number;
  quantity: number;
};

// Adapter from raw fulfillment-plan-item redux state to the generic
// SerializableLineItem shape useSerialPrintGuard/useHasMissingRequiredSerials
// expect. Mirrors the item/product lookup already done in
// scan-to-verify-body/connected.js's getItems selector, so both the scan-page
// print button and Order Details print points read the exact same requiresSerialNumber
// determination.
export const useFulfillmentPlanSerializableItems = (fulfillmentPlanId?: string): readonly SerializableLineItem[] => {
  const fulfillmentPlanItems = useSelector((state) =>
    fulfillmentPlanId
      ? ((Global.selectEntitiesFulfillmentPlanProperty(state, { fulfillmentPlanId, property: 'items' }) as
          | FulfillmentPlanItem[]
          | undefined) ?? emptyArray)
      : emptyArray,
  ) as FulfillmentPlanItem[];

  const productIds = useMemo(() => fulfillmentPlanItems.map((item) => item.productId), [fulfillmentPlanItems]);

  const entityProducts = useSelector((state) => Global.selectEntitiesProductsByIds(state, { productIds })) as (
    | ProductWithOptions
    | undefined
  )[];

  return useMemo(
    () =>
      fulfillmentPlanItems
        // A null fulfillmentPlanItemId can't be looked up in SerialNumberProvider's map,
        // and Number(null) would otherwise coerce to 0 — a phantom itemId that could
        // falsely appear to be missing a serial.
        .filter((item) => item.fulfillmentPlanItemId != null)
        .map((item) => {
          const product = entityProducts.find((prod) => prod && Number(prod.productId) === Number(item.productId));
          const requiresSerialNumber = Boolean(
            product?.productOptions?.find((option) => option.name === RequireSerialNumberName)?.value === RequireSerialNumberEnabledValue,
          );
          return {
            itemId: Number(item.fulfillmentPlanItemId),
            itemQuantity: item.quantity,
            requiresSerialNumber,
          };
        }),
    [fulfillmentPlanItems, entityProducts],
  );
};
